import {
  MEDIA_POPUP_BROWSE_DEVIATION_STREAM_ID,
  MEDIA_POPUP_BROWSE_SEARCH_DEVIATION_STREAM_ID,
  MEDIA_POPUP_GALLECTION_DEVIATION_STREAM_GROUP,
  MEDIA_POPUP_GALLECTION_STREAM_GROUP,
  MEDIA_POPUP_CUSTOM_DEVIATION_SET_STREAM_ID,
  MEDIA_POPUP_GALLECTION_DEVIATION_SEARCH_STREAM_GROUP,
  MEDIA_POPUP_POLL_STREAM_ID,
  MEDIA_POPUP_STATUS_STREAM_ID,
  MEDIA_POPUP_JOURNAL_STREAM_ID,
  MEDIA_POPUP_POSTS_STREAM_ID,
  MEDIA_POPUP_TIER_STREAM_ID,
  MEDIA_POPUP_COMMISSION_STREAM_ID,
  DEVIATION_TYPES_ALLOWED_AS_FOLDER_COVER,
} from './constants';
import { MediaSourceType } from './types';
import { PapiGallection, PapiDeviation, PapiUser } from '@wix/da-papi-types';
import { ALL_FOLDER_ID, SCRAPS_FOLDER_ID } from '@wix/da-url';
import { GallectionType } from '../types/gallection';
import { getDeviationType } from '../DeviationViews/_helpers';
import { DeviationTypes } from '../types/deviation';
import { getIsGif } from '../redux/deviations/selectors';
import {
  galleryFolderSchema,
  collectionFolderSchema,
  stashFolderSchema,
} from '../redux/normalizr/schemas/folder';
import { isSpecialDefaultGallection } from '../helpers/gallections';
import { isGroup } from '../User/helpers';

export function getGallectionStreamId(
  mediaSource?: MediaSourceType,
  folderId?: number /* for subfolders stream */
) {
  const folderSuffix = folderId ? `-${folderId}` : '';
  return `${MEDIA_POPUP_GALLECTION_STREAM_GROUP}-${mediaSource}${folderSuffix}`;
}

export function getBrowseDeviationStreamId(searchQuery?: string) {
  return searchQuery
    ? MEDIA_POPUP_BROWSE_SEARCH_DEVIATION_STREAM_ID
    : MEDIA_POPUP_BROWSE_DEVIATION_STREAM_ID;
}
export function getPostStreamIdFromMediaSource(mediaSource?: MediaSourceType) {
  switch (mediaSource) {
    case MediaSourceType.POSTS:
      return MEDIA_POPUP_POSTS_STREAM_ID;
    case MediaSourceType.POLL:
      return MEDIA_POPUP_POLL_STREAM_ID;
    case MediaSourceType.STATUS:
      return MEDIA_POPUP_STATUS_STREAM_ID;
    case MediaSourceType.JOURNAL:
      return MEDIA_POPUP_JOURNAL_STREAM_ID;
    default:
      return MEDIA_POPUP_JOURNAL_STREAM_ID;
  }
}

export function getCustomDeviationSetStreamId() {
  return MEDIA_POPUP_CUSTOM_DEVIATION_SET_STREAM_ID;
}

export function getTierStreamId() {
  return MEDIA_POPUP_TIER_STREAM_ID;
}

export function getCommissionStreamId() {
  return MEDIA_POPUP_COMMISSION_STREAM_ID;
}

export function getGallectionDeviationStreamId(
  mediaSource?: MediaSourceType,
  folderId?: number,
  searchQuery?: string
) {
  if (searchQuery) {
    return `${MEDIA_POPUP_GALLECTION_DEVIATION_SEARCH_STREAM_GROUP}-${mediaSource}$`;
  }
  return `${MEDIA_POPUP_GALLECTION_DEVIATION_STREAM_GROUP}-${mediaSource}${
    folderId !== undefined ? `-${folderId}` : ''
  }`;
}

export function getTierDeviationsStreamId(mediaSource?: MediaSourceType, tierId?: number) {
  return `${mediaSource}-${tierId}`;
}

export function canGallectionHaveSubfolders(gallection: PapiGallection) {
  if (
    gallection.type === 'collection' ||
    gallection.type === 'private_collection' ||
    // TODO: this not entirely correct, revisit once backend is updated
    gallection.type === 'stash' ||
    gallection.name === 'Featured' ||
    gallection.folderId === ALL_FOLDER_ID ||
    gallection.folderId === SCRAPS_FOLDER_ID
  ) {
    return false;
  }
  return true;
}

// special gallections that can't be moved, removed, renamed
// their deviations can't be moved, or removed either
export function isSpecialFullyLockedFolder(gallection?: PapiGallection) {
  if (gallection?.folderId === ALL_FOLDER_ID || gallection?.folderId === SCRAPS_FOLDER_ID) {
    return true;
  }
  return false;
}

// special gallections that can't be moved, removed, or renamed
export function isSpecialStuckGallection(gallection: PapiGallection) {
  return isSpecialDefaultGallection(gallection) || isSpecialFullyLockedFolder(gallection);
}

export function getMediaSourceFromGallectionType(gallectionType: GallectionType) {
  return {
    gallery: MediaSourceType.GALLERY,
    collection: MediaSourceType.COLLECTION,
    private_collection: MediaSourceType.PRIVATE_COLLECTION,
  }[gallectionType];
}

export function getCanRenameGallection(gallection: PapiGallection) {
  return (
    !isSpecialStuckGallection(gallection) &&
    !isSpecialFullyLockedFolder(gallection) &&
    getCanEditGallection(gallection)
  );
}

export function getCanChangeGallectionDescription(gallection: PapiGallection) {
  return (
    !isSpecialFullyLockedFolder(gallection) &&
    // check privs for groups
    getCanEditGallection(gallection)
  );
}

export function getCanMoveGallection(gallection: PapiGallection) {
  return (
    !isSpecialStuckGallection(gallection) &&
    !isSpecialFullyLockedFolder(gallection) &&
    getCanEditGallection(gallection)
  );
}

export function getCanSetGallectionCover(gallection: PapiGallection) {
  if (isSpecialFullyLockedFolder(gallection)) {
    return false;
  }
  if (['stash'].includes(gallection.type)) {
    return false;
  }

  return getCanEditGallection(gallection);
}

export function getCanEditGallection(gallection?: PapiGallection) {
  return !!gallection?.permissions?.editFolder;
}

export function getCanRemoveGallection(gallection: PapiGallection) {
  return (
    !isSpecialStuckGallection(gallection) &&
    !isSpecialFullyLockedFolder(gallection) &&
    !!gallection.permissions?.deleteFolder
  );
}

export function getCanLabelDeviationsInThisGallection(
  gruser?: PapiUser,
  gallection?: PapiGallection
) {
  return (
    !!gruser &&
    !isGroup(gruser) &&
    !!gallection &&
    gallection.type === 'gallery' &&
    gallection.size > 0
  );
}

function isGallection(item: any): item is PapiGallection {
  return item && item.hasOwnProperty('folderId');
}

export function getGallectionType(
  item: MediaSourceType | PapiGallection | GallectionType | undefined
): GallectionType | undefined {
  if (isGallection(item)) {
    return item.type as GallectionType;
  }

  switch (item) {
    case 'gallery':
      return GallectionType.GALLERY;
    case 'private_collection':
      return GallectionType.PRIVATE_COLLECTION;
    case 'collection':
      return GallectionType.COLLECTION;
    case 'stash':
      return GallectionType.STASH;
    default:
      return;
  }
}

export function getFolderIdsDisabledForDeviationCopy(
  folders,
  currentFolderId,
  deviation?: PapiDeviation
) {
  if (deviation && !canMakeDeviationPremium(deviation)) {
    return getPremiumFolderIds(folders);
  }
  return currentFolderId !== ALL_FOLDER_ID ? getPremiumFolderIds(folders) : [];
}

export function getPremiumFolderIds(folders: any[]) {
  return folders.filter(g => !!g.premiumFolderData).map(g => g.folderId);
}

export function canMakeDeviationPremium(deviation: PapiDeviation) {
  const deviationType = getDeviationType(deviation);
  return (
    (deviationType === DeviationTypes.IMAGE && !getIsGif(deviation)) ||
    deviationType === DeviationTypes.LITERATURE
  );
}

export function getGallectionSchemaByType(gallection?: MediaSourceType) {
  const gallectionType = getGallectionType(gallection);
  switch (gallectionType) {
    case GallectionType.STASH:
      return stashFolderSchema;
    case GallectionType.COLLECTION:
    case GallectionType.PRIVATE_COLLECTION:
      return collectionFolderSchema;
    case GallectionType.GALLERY:
    default:
      return galleryFolderSchema;
  }
}

export function canUseAsFolderCover(deviation: PapiDeviation) {
  return (
    DEVIATION_TYPES_ALLOWED_AS_FOLDER_COVER.includes(getDeviationType(deviation)) &&
    !getIsGif(deviation)
  );
}
